import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Store, 
  MapPin, 
  Phone, 
  Globe, 
  Clock,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  Loader2
} from 'lucide-react';
import BottomNav from '@/components/BottomNav';

const COMMERCE_CATEGORIES = [
  { id: 'restaurant', name: 'Restaurant / Bar', icon: '🍽️' },
  { id: 'boutique', name: 'Boutique / Commerce', icon: '🛍️' },
  { id: 'service', name: 'Service', icon: '🔧' },
  { id: 'beaute', name: 'Beauté / Bien-être', icon: '💅' },
  { id: 'sante', name: 'Santé', icon: '🏥' },
  { id: 'loisirs', name: 'Loisirs / Sport', icon: '⚽' },
  { id: 'hebergement', name: 'Hébergement', icon: '🏨' },
  { id: 'artisanat', name: 'Artisanat', icon: '🎨' },
  { id: 'autre', name: 'Autre', icon: '📦' }
];

const CommerceSettingsPage = () => {
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [isCommerce, setIsCommerce] = useState(user?.is_commerce || false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyForm, setVerifyForm] = useState({
    business_name: '',
    business_registration: '',
    business_type: '',
    additional_info: ''
  });
  const [form, setForm] = useState({
    commerce_name: user?.commerce_name || '',
    commerce_description: user?.commerce_description || '',
    commerce_category: user?.commerce_category || '',
    commerce_address: user?.commerce_address || '',
    commerce_phone: user?.commerce_phone || '',
    commerce_website: user?.commerce_website || '',
    commerce_hours: user?.commerce_hours || ''
  });

  useEffect(() => {
    if (user) {
      setIsCommerce(user.is_commerce || false);
      setForm({
        commerce_name: user.commerce_name || '',
        commerce_description: user.commerce_description || '',
        commerce_category: user.commerce_category || '',
        commerce_address: user.commerce_address || '',
        commerce_phone: user.commerce_phone || '',
        commerce_website: user.commerce_website || '',
        commerce_hours: user.commerce_hours || ''
      });
      setVerifyForm(prev => ({
        ...prev,
        business_name: user.commerce_name || '',
        business_type: user.commerce_category || ''
      }));
    }
  }, [user]);

  // Check verification status
  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (!user?.is_commerce) return;
      try {
        const response = await api.get('/commerce/verify/status');
        setVerificationStatus(response.data);
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    };
    checkVerificationStatus();
  }, [api, user?.is_commerce]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isCommerce && !form.commerce_name.trim()) {
      toast.error(language === 'cr' ? 'Non lo komèrs lé obligatwar' : 'Le nom du commerce est requis');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/users/${user.user_id}/commerce`, {
        ...form,
        is_commerce: isCommerce
      });
      
      await refreshUser?.();
      toast.success(language === 'cr' ? 'Profil komèrs mizajour !' : 'Profil commerce mis à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async () => {
    if (!verifyForm.business_name.trim() || !verifyForm.business_type) {
      toast.error(language === 'cr' ? 'Rampli tout bann shan obligatwar' : 'Veuillez remplir tous les champs obligatoires');
      return;
    }

    setVerifyLoading(true);
    try {
      await api.post('/commerce/verify', verifyForm);
      toast.success(
        language === 'cr' 
          ? 'Domand vérifikasyon soumèt ! Ou va ét notifié apré.' 
          : 'Demande de vérification soumise ! Vous serez notifié une fois traitée.'
      );
      setShowVerifyDialog(false);
      // Refresh status
      const response = await api.get('/commerce/verify/status');
      setVerificationStatus(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setVerifyLoading(false);
    }
  };

  const getCategoryInfo = (categoryId) => {
    return COMMERCE_CATEGORIES.find(c => c.id === categoryId) || COMMERCE_CATEGORIES[8];
  };

  return (
    <div className="min-h-screen tropic-bg pb-28 md:pb-12 md:pt-16">
      {/* Header */}
      <header className="sticky top-0 md:top-16 z-40 glass border-b">
        <div className="container mx-auto max-w-2xl px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Profil Commerce</h1>
              <p className="text-xs text-muted-foreground">Gérez votre activité locale</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-2xl px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Commerce Toggle */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Store className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Activer le profil commerce</p>
                    <p className="text-sm text-muted-foreground">
                      Mettez en avant votre activité locale
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={isCommerce}
                  onCheckedChange={setIsCommerce}
                  data-testid="commerce-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {isCommerce && (
            <>
              {/* Verification Status */}
              <Card className={user?.is_verified_commerce ? 'border-green-500/50' : 'border-orange-500/50'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {user?.is_verified_commerce ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <div className="flex-1">
                          <p className="font-medium text-green-700 dark:text-green-400">
                            {language === 'cr' ? 'Komèrs vérifié' : 'Commerce vérifié'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {language === 'cr' 
                              ? 'Out komèrs lé vérifié èk afishé èk in baz' 
                              : 'Votre commerce est vérifié et affiché avec un badge'}
                          </p>
                        </div>
                        <Badge className="bg-green-500">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          {language === 'cr' ? 'Vérifié' : 'Vérifié'}
                        </Badge>
                      </>
                    ) : verificationStatus?.status === 'pending' ? (
                      <>
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                        <div className="flex-1">
                          <p className="font-medium text-blue-700 dark:text-blue-400">
                            {language === 'cr' ? 'Vérifikasyon an kour' : 'Vérification en cours'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {language === 'cr' 
                              ? 'Out domand lé an trin dét trété' 
                              : 'Votre demande est en cours de traitement'}
                          </p>
                        </div>
                        <Badge variant="outline" className="border-blue-500 text-blue-500">
                          {language === 'cr' ? 'An kour' : 'En cours'}
                        </Badge>
                      </>
                    ) : verificationStatus?.status === 'rejected' ? (
                      <>
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <div className="flex-1">
                          <p className="font-medium text-red-700 dark:text-red-400">
                            {language === 'cr' ? 'Vérifikasyon refuzé' : 'Vérification refusée'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {verificationStatus?.reviewer_notes || (language === 'cr' ? 'Éséy ankor' : 'Veuillez réessayer')}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowVerifyDialog(true)}
                        >
                          {language === 'cr' ? 'Rééséyé' : 'Réessayer'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-5 w-5 text-orange-500" />
                        <div className="flex-1">
                          <p className="font-medium text-orange-700 dark:text-orange-400">
                            {language === 'cr' ? 'Pa ankor vérifié' : 'Non vérifié'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {language === 'cr' 
                              ? 'Domand la vérifikasyon pou gagn lo baz' 
                              : 'Demandez la vérification pour obtenir le badge'}
                          </p>
                        </div>
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => setShowVerifyDialog(true)}
                          data-testid="request-verification-btn"
                        >
                          <ShieldCheck className="h-4 w-4 mr-1" />
                          {language === 'cr' ? 'Vérifyé' : 'Vérifier'}
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Commerce Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informations du commerce</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="commerce_name">Nom du commerce *</Label>
                    <Input
                      id="commerce_name"
                      placeholder="Ex: Le Bon Pain"
                      value={form.commerce_name}
                      onChange={(e) => setForm({ ...form, commerce_name: e.target.value })}
                      data-testid="commerce-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commerce_category">Catégorie</Label>
                    <Select 
                      value={form.commerce_category} 
                      onValueChange={(val) => setForm({ ...form, commerce_category: val })}
                    >
                      <SelectTrigger data-testid="commerce-category-select">
                        <SelectValue placeholder="Sélectionnez une catégorie" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMERCE_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span>{cat.icon}</span>
                              <span>{cat.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commerce_description">Description</Label>
                    <Textarea
                      id="commerce_description"
                      placeholder="Décrivez votre activité..."
                      value={form.commerce_description}
                      onChange={(e) => setForm({ ...form, commerce_description: e.target.value })}
                      rows={3}
                      data-testid="commerce-description-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Contact Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Coordonnées</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="commerce_address" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Adresse
                    </Label>
                    <Input
                      id="commerce_address"
                      placeholder="Ex: 12 Rue du Commerce, 97400 Saint-Denis"
                      value={form.commerce_address}
                      onChange={(e) => setForm({ ...form, commerce_address: e.target.value })}
                      data-testid="commerce-address-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commerce_phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Téléphone
                    </Label>
                    <Input
                      id="commerce_phone"
                      placeholder="Ex: 0262 XX XX XX"
                      value={form.commerce_phone}
                      onChange={(e) => setForm({ ...form, commerce_phone: e.target.value })}
                      data-testid="commerce-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commerce_website" className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Site web
                    </Label>
                    <Input
                      id="commerce_website"
                      placeholder="Ex: https://www.moncommerce.re"
                      value={form.commerce_website}
                      onChange={(e) => setForm({ ...form, commerce_website: e.target.value })}
                      data-testid="commerce-website-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commerce_hours" className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Horaires d'ouverture
                    </Label>
                    <Textarea
                      id="commerce_hours"
                      placeholder="Ex: Lun-Ven: 8h-18h, Sam: 9h-12h"
                      value={form.commerce_hours}
                      onChange={(e) => setForm({ ...form, commerce_hours: e.target.value })}
                      rows={2}
                      data-testid="commerce-hours-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full"
            disabled={loading}
            data-testid="save-commerce-btn"
          >
            {loading ? (language === 'cr' ? 'An trin anrézistr...' : 'Enregistrement...') : (language === 'cr' ? 'Anrézistr' : 'Enregistrer')}
          </Button>
        </form>

        {/* Verification Dialog */}
        <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                {language === 'cr' ? 'Domand vérifikasyon' : 'Demande de vérification'}
              </DialogTitle>
              <DialogDescription>
                {language === 'cr' 
                  ? 'Rampli sa form la pou fénou vérifyé out komèrs. Ou va résevwar in notifikasyon apré.'
                  : 'Remplissez ce formulaire pour faire vérifier votre commerce. Vous recevrez une notification une fois la demande traitée.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="verify_business_name">
                  {language === 'cr' ? 'Non lo komèrs *' : 'Nom du commerce *'}
                </Label>
                <Input
                  id="verify_business_name"
                  value={verifyForm.business_name}
                  onChange={(e) => setVerifyForm({ ...verifyForm, business_name: e.target.value })}
                  placeholder={language === 'cr' ? 'Ex: Lo Bon Pin' : 'Ex: Le Bon Pain'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="verify_business_type">
                  {language === 'cr' ? 'Tip d\'aktivité *' : 'Type d\'activité *'}
                </Label>
                <Select 
                  value={verifyForm.business_type} 
                  onValueChange={(val) => setVerifyForm({ ...verifyForm, business_type: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'cr' ? 'Shwazi in tip' : 'Sélectionnez un type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMERCE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <span className="flex items-center gap-2">
                          <span>{cat.icon}</span>
                          <span>{cat.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="verify_siret">
                  {language === 'cr' ? 'Niméro SIRET (opsyonèl)' : 'Numéro SIRET (optionnel)'}
                </Label>
                <Input
                  id="verify_siret"
                  value={verifyForm.business_registration}
                  onChange={(e) => setVerifyForm({ ...verifyForm, business_registration: e.target.value })}
                  placeholder="XXX XXX XXX XXXXX"
                />
                <p className="text-xs text-muted-foreground">
                  {language === 'cr' 
                    ? 'Fournir out SIRET i aksélèr la vérifikasyon'
                    : 'Fournir votre SIRET accélère la vérification'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="verify_info">
                  {language === 'cr' ? 'Informasyon siplémentèr' : 'Informations supplémentaires'}
                </Label>
                <Textarea
                  id="verify_info"
                  value={verifyForm.additional_info}
                  onChange={(e) => setVerifyForm({ ...verifyForm, additional_info: e.target.value })}
                  placeholder={language === 'cr' 
                    ? 'Tousafèr ki pé édnou vérifyé out komèrs...'
                    : 'Tout ce qui peut nous aider à vérifier votre commerce...'}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVerifyDialog(false)}>
                {language === 'cr' ? 'Anilé' : 'Annuler'}
              </Button>
              <Button onClick={handleVerificationSubmit} disabled={verifyLoading}>
                {verifyLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {language === 'cr' ? 'Soumèt' : 'Soumettre'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>

      <BottomNav />
    </div>
  );
};

export default CommerceSettingsPage;
